import React, { memo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// components
import Header from 'shared/components/Header/MobileHeader';
import AuthModalWrapper from 'shared/components/AuthModal/AuthModalWrapper';
import AppMigrationTopBanner from 'shared/components/AppMigration/AppMigrationTopBanner';

// provider
import MobileActionProvider from 'shared/components/Header/MobileAction';
import MobileLayoutContext from './MobileLayoutContext';

// styles
import { Container, StickyHeaderWrapper } from './styles';

// defs
import { IHeaderOptions } from 'shared/store/app/types';
import { isWebView } from 'shared/helpers/utils/url';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useEligibleSSRVariantName } from 'shared/components/ExperimentV2/helpers';

// store
import { getWebAppType } from 'shared/store/app/selectors';
import { getIsAppMigrationTopBannerDismissed } from 'shared/store/appMigration/selectors';

// helpers
import { shouldShowAppMigrationTopBanner } from 'shared/components/AppMigration/helpers';

// constants
import {
  APP_MIGRATION_EXP,
  APP_MIGRATION_EXP_VARIANT_A,
} from 'shared/components/AppMigration/constants';

interface IMobileHeaderProviderProps {
  children: React.ReactNode;
  className?: string;
  headerProps: IHeaderOptions;
  isHeaderVisible: boolean;
}

const DEFAULT_STYLES = {};

function MobileHeaderProvider({
  children,
  className,
  headerProps,
  isHeaderVisible,
}: IMobileHeaderProviderProps) {
  const [headerStyles, setHeaderStyles] = useState<React.CSSProperties>(DEFAULT_STYLES);
  const [HeaderContent, setHeaderContent] = useState<React.ReactNode | undefined>(undefined);
  const [HeaderActions, setHeaderActions] = useState<React.ReactNode | undefined>(undefined);

  const setCSS = useCallback((styles: React.CSSProperties) => setHeaderStyles(styles), []);
  const resetCSS = useCallback(() => setHeaderStyles(DEFAULT_STYLES), []);

  const location = useLocation();
  const { pathname, search } = location;
  const webAppType = useSelector(getWebAppType);
  const isTopBannerDismissed = useSelector(getIsAppMigrationTopBannerDismissed);
  const appMigrationEnabled = !!useRemoteConfig('appMigrationEnabled');
  const isTopBannerVisible = !!useRemoteConfig('appMigrationTopBannerVisible');
  const isRewardsPageEnabled = useRemoteConfig('rewardsPageEnabled') || false;

  const { isEligible = false, variant = '' } = useEligibleSSRVariantName(APP_MIGRATION_EXP);
  const isAppMigrationVariantA = isEligible && variant === APP_MIGRATION_EXP_VARIANT_A;

  const { headerMerge } = headerProps;

  const shouldShowHeader = !isWebView(location) && isHeaderVisible;

  const isBannerVisible = shouldShowAppMigrationTopBanner({
    isTopBannerDismissed,
    webAppType,
    appMigrationEnabled,
    isTopBannerVisible,
    isAppMigrationVariantA,
    pathname,
    search,
    isWebView: isWebView(location),
  });

  return (
    <Container
      allowPadding={shouldShowHeader && headerMerge === false}
      className={className}
      style={headerStyles}
      id="app-container"
      isRewardsPageEnabled={isRewardsPageEnabled}
    >
      <AuthModalWrapper>
        <MobileActionProvider>
          <StickyHeaderWrapper hasBanner={isBannerVisible}>
            {isBannerVisible && <AppMigrationTopBanner />}
            {shouldShowHeader && (
              <Header
                {...headerProps}
                HeaderContent={HeaderContent}
                MobileActions={HeaderActions}
              />
            )}
          </StickyHeaderWrapper>
          <MobileLayoutContext.Provider
            value={{ setHeaderContent, setHeaderActions, setCSS, resetCSS }}
          >
            {children}
          </MobileLayoutContext.Provider>
        </MobileActionProvider>
      </AuthModalWrapper>
    </Container>
  );
}

export default memo(MobileHeaderProvider);
